import { doTien } from './so-tien.ts';
import type { BuocThoiGian } from './types-thong-ke.ts';

/**
 * Đọc và hiện số của Nhật ký sử dụng AI và biểu đồ — port `thong-ke/so.ts` native.
 *
 * Khác native: mọi hàm hiện số nhận `locale` (ADR-UI-0010). `gonSo` dùng `notation: 'compact'` của `Intl` thay ba hậu
 * tố tiếng Việt viết tay (`tỷ`, `tr`, `ng`) — hậu tố đi theo ngôn ngữ, không có chuỗi giao diện trong tầng thuần.
 */

export { doTien };

/** Chỗ không có dữ liệu. Giữ y dấu của `thoi-gian.ts` để hai màn nói một kiểu. */
export const TRONG = '—';

const LOCALE_MAC_DINH = 'vi-VN';

export function soNguyen(gt: number | null | undefined, locale: string = LOCALE_MAC_DINH): string {
  if (gt === null || gt === undefined || !Number.isFinite(gt)) return TRONG;
  return gt.toLocaleString(locale, { maximumFractionDigits: 0 });
}

/** `1234567` → `1,2 Tr` (vi) / `1.2M` (en). Trục biểu đồ không có chỗ cho chín chữ số. */
export function gonSo(gt: number, locale: string = LOCALE_MAC_DINH): string {
  if (Math.abs(gt) < 1e4) return gt.toLocaleString(locale, { maximumFractionDigits: 2 });
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(gt);
}

/**
 * Tỷ lệ dạng `12,5 %`, hoặc `—` khi mẫu số bằng 0: "không có lượt nào" và "không lượt nào lỗi" là hai kết luận khác
 * nhau, và chỉ cái thứ hai là tin tốt.
 */
export function tyLe(phan: number, tren: number, locale: string = LOCALE_MAC_DINH): string {
  if (!Number.isFinite(phan) || !Number.isFinite(tren) || tren <= 0) return TRONG;
  return (phan / tren).toLocaleString(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** `05/09` — ngày và tháng, giữ thứ tự `dd/MM` ở cả hai ngôn ngữ. */
function phanNgay(moc: number, locale: string): string {
  const phan = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).formatToParts(moc);
  const lay = (loai: Intl.DateTimeFormatPartTypes) => phan.find((p) => p.type === loai)?.value ?? '';
  return `${lay('day')}/${lay('month')}`;
}

/** Nhãn một mốc trên trục thời gian, theo bước gom mà server đã dùng: `14h 01/10`, `01/10`, `10/2026`. */
export function nhanMoc(iso: string, buoc: BuocThoiGian, locale: string = LOCALE_MAC_DINH): string {
  const moc = Date.parse(iso);
  if (Number.isNaN(moc)) return TRONG;
  if (buoc === 'month') {
    const phan = new Intl.DateTimeFormat(locale, { month: '2-digit', year: 'numeric' }).formatToParts(moc);
    const lay = (loai: Intl.DateTimeFormatPartTypes) => phan.find((p) => p.type === loai)?.value ?? '';
    return `${lay('month')}/${lay('year')}`;
  }
  if (buoc === 'hour') return `${phanGio(moc, locale)}:00 ${phanNgay(moc, locale)}`;
  return phanNgay(moc, locale);
}

/**
 * Giờ hai chữ số, 24 giờ. Lấy riêng phần `hour`: `format()` của `vi-VN` với chỉ `hour` trả `10 giờ`, ghép thêm `:00`
 * là ra `10 giờ:00`.
 */
function phanGio(moc: number, locale: string): string {
  const phan = new Intl.DateTimeFormat(locale, { hour: '2-digit', hourCycle: 'h23' }).formatToParts(moc);
  return phan.find((p) => p.type === 'hour')?.value ?? '';
}

/**
 * Tiêu đề thẻ chú thích của một mốc — đầy đủ hơn nhãn trục, vì thẻ có chỗ: `10:00–11:00 · 02/11/2026`, `02/11/2026`,
 * `11/2026`. Ngày giữ `dd/MM/yyyy` ở cả hai ngôn ngữ (ADR-UI-0010).
 */
export function nhanMocDayDu(iso: string, buoc: BuocThoiGian, locale: string = LOCALE_MAC_DINH): string {
  const moc = Date.parse(iso);
  if (Number.isNaN(moc)) return TRONG;
  if (buoc === 'month') return nhanMoc(iso, buoc, locale);
  const phan = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(moc);
  const lay = (loai: Intl.DateTimeFormatPartTypes) => phan.find((p) => p.type === loai)?.value ?? '';
  const ngay = `${lay('day')}/${lay('month')}/${lay('year')}`;
  if (buoc === 'day') return ngay;
  return `${phanGio(moc, locale)}:00–${phanGio(moc + 3_600_000, locale)}:00 · ${ngay}`;
}
